/**
 * Active-read clock for a dashboard page.
 *
 * Ported from the audit report's `report_dwell`
 * (packages/server/src/services/siteAuditReport.service.ts): a second counts
 * only while the tab is visible AND input landed inside the recency window,
 * which is "what keeps a parked tab from reading as a 12-minute read".
 *
 * Two deliberate differences from that inline version:
 *
 *  - The clock is INJECTED rather than read from `Date.now()` inside, so the
 *    gating rule is unit-testable without fake timers and without a live
 *    `setInterval`. The React hook owns the interval; this owns the rule.
 *  - The recency comparison is `<=` where the report uses `<`. On a
 *    deterministic clock ticking exactly every `tickMs`, the strict form drops
 *    the tick that lands on the boundary and a 10s window credits 9s. The
 *    intent is "the first 10 seconds of a visit count as engaged", and `<=`
 *    is what implements it.
 *
 * Arrival seeds the input clock, so a reader who lands and reads without
 * touching anything is counted as engaged for the first window rather than
 * being scored zero.
 */
export interface DwellSnapshot {
  /** Milliseconds the reader was plausibly ON the page, not merely parked. */
  active_ms: number;
  /** Wall clock since the tracker was created, regardless of engagement. */
  total_ms: number;
  /** Deepest scroll reached, 0-100. A page too short to scroll reports 100. */
  max_scroll_pct: number;
}

export interface DwellTracker {
  /** Called once per `tickMs` by the owner; accrues only when engaged. */
  tick(visible: boolean): void;
  noteInput(): void;
  noteScroll(pct: number): void;
  snapshot(): DwellSnapshot;
}

export function createDwellTracker({
  now,
  tickMs = 1000,
  activeInputWindowMs = 10_000,
}: {
  now: () => number;
  tickMs?: number;
  activeInputWindowMs?: number;
}): DwellTracker {
  const startedAt = now();
  let lastInputAt = startedAt;
  let activeMs = 0;
  let maxScrollPct = 0;

  return {
    tick(visible: boolean) {
      if (!visible) return;
      if (now() - lastInputAt > activeInputWindowMs) return;
      activeMs += tickMs;
    },
    noteInput() {
      lastInputAt = now();
    },
    noteScroll(pct: number) {
      if (!Number.isFinite(pct)) return;
      const clamped = Math.max(0, Math.min(100, Math.round(pct)));
      if (clamped > maxScrollPct) maxScrollPct = clamped;
    },
    snapshot() {
      return { active_ms: activeMs, total_ms: now() - startedAt, max_scroll_pct: maxScrollPct };
    },
  };
}
